// レイヤースタイル用のシェーダ。すべて文書サイズの FBO 同士で動く。
// - BLUR: 分離ガウス (R チャンネル = α)。uDir で方向、uRadius でサイズ
// - SHAPE: α の取り出し / オフセット / 反転 / しきい値 (スプレッド、拡張・収縮の近似)
// - COLORIZE: α × 色 × 不透明度 (ストレート RGBA)。uMaskMode でベース α による内側限定
// - BEVEL: ぼかした α の勾配から光源方向のハイライト / シャドウを作る

export const BLUR_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;
uniform sampler2D uSrc;
uniform vec2 uDocSize;
uniform vec2 uDir;       // (1,0) or (0,1)
uniform float uRadius;   // px
out vec4 outColor;
void main() {
  float r = max(uRadius, 0.0);
  if (r < 0.01) { outColor = texture(uSrc, vPos / uDocSize); return; }
  float sigma = r * 0.5;
  int taps = int(min(ceil(r), 64.0));
  float sum = 0.0;
  float wsum = 0.0;
  for (int i = -64; i <= 64; i++) {
    if (i < -taps || i > taps) continue;
    float x = float(i);
    float w = exp(-x * x / (2.0 * sigma * sigma));
    vec2 p = (vPos + uDir * x) / uDocSize;
    float a = (p.x < 0.0 || p.y < 0.0 || p.x > 1.0 || p.y > 1.0) ? 0.0 : texture(uSrc, p).r;
    sum += a * w;
    wsum += w;
  }
  outColor = vec4(sum / wsum, 0.0, 0.0, 1.0);
}
`

export const SHAPE_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;
uniform sampler2D uSrc;      // 入力 (α は .a を使う / uFromR == 1 なら .r)
uniform vec2 uDocSize;
uniform vec2 uOffset;        // 文書 px
uniform int uFromR;
uniform int uInvert;
uniform float uLo;           // しきい値 (smoothstep の下端)
uniform float uHi;           // 上端
out vec4 outColor;
void main() {
  vec2 p = (vPos - uOffset) / uDocSize;
  float a = 0.0;
  if (p.x >= 0.0 && p.y >= 0.0 && p.x <= 1.0 && p.y <= 1.0) {
    vec4 t = texture(uSrc, p);
    a = uFromR == 1 ? t.r : t.a;
  }
  if (uInvert == 1) a = 1.0 - a;
  if (uHi > uLo) a = smoothstep(uLo, uHi, a);
  outColor = vec4(a, 0.0, 0.0, 1.0);
}
`

export const COLORIZE_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;
uniform sampler2D uAlpha;    // R = α
uniform sampler2D uBase;     // ベース (α でマスク)
uniform sampler2D uGrad;     // 256x1 グラデーション LUT (uMode == 2)
uniform vec2 uDocSize;
uniform vec3 uColor;
uniform float uOpacity;
uniform int uMaskMode;       // 0: なし, 1: ベース α で乗算 (内側), 2: (1 - ベース α) で乗算 (外側)
uniform int uMode;           // 0: 単色, 2: グラデーション (uA→uB), 3: パターン
uniform vec2 uA;
uniform vec2 uB;
uniform int uRadial;
uniform sampler2D uPattern;
uniform vec2 uPatSize;
out vec4 outColor;
void main() {
  vec2 uv = vPos / uDocSize;
  float a = texture(uAlpha, uv).r;
  float ba = texture(uBase, uv).a;
  if (uMaskMode == 1) a *= ba;
  else if (uMaskMode == 2) a *= (1.0 - ba);
  vec3 c = uColor;
  if (uMode == 2) {
    vec2 d = uB - uA;
    float t;
    if (uRadial == 1) t = length(vPos - uA) / max(length(d), 1e-3);
    else t = dot(vPos - uA, d) / max(dot(d, d), 1e-6);
    t = clamp(t, 0.0, 1.0);
    c = texture(uGrad, vec2((t * 255.0 + 0.5) / 256.0, 0.5)).rgb;
  } else if (uMode == 3) {
    vec4 pc = texture(uPattern, fract(vPos / uPatSize));
    c = pc.rgb;
    a *= pc.a;
  }
  outColor = vec4(c, clamp(a * uOpacity, 0.0, 1.0));
}
`

export const BEVEL_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;
uniform sampler2D uHeight;   // R = ぼかした α (高さ)
uniform sampler2D uBase;
uniform vec2 uDocSize;
uniform vec3 uLight;         // 光源方向 (正規化)
uniform float uDepth;        // 強さ
uniform int uStyle;          // 0 inner (内側のみ), 1 outer (外側のみ), 2 emboss (両方)
uniform vec3 uHiColor;
uniform float uHiOpacity;
uniform vec3 uShColor;
uniform float uShOpacity;
uniform int uWhich;          // 0 = ハイライト出力, 1 = シャドウ出力
out vec4 outColor;
void main() {
  vec2 px = 1.0 / uDocSize;
  vec2 uv = vPos * px;
  float hL = texture(uHeight, uv - vec2(px.x, 0.0)).r;
  float hR = texture(uHeight, uv + vec2(px.x, 0.0)).r;
  float hU = texture(uHeight, uv - vec2(0.0, px.y)).r;
  float hD = texture(uHeight, uv + vec2(0.0, px.y)).r;
  vec3 n = normalize(vec3((hL - hR) * uDepth, (hU - hD) * uDepth, 1.0));
  float l = dot(n, uLight);
  float ba = texture(uBase, uv).a;
  float region = uStyle == 0 ? ba : (uStyle == 1 ? (1.0 - ba) : 1.0);
  float hi = clamp(l - dot(vec3(0.0, 0.0, 1.0), uLight), 0.0, 1.0);
  float sh = clamp(dot(vec3(0.0, 0.0, 1.0), uLight) - l, 0.0, 1.0);
  if (uWhich == 0) outColor = vec4(uHiColor, clamp(hi * 2.0, 0.0, 1.0) * uHiOpacity * region);
  else outColor = vec4(uShColor, clamp(sh * 2.0, 0.0, 1.0) * uShOpacity * region);
}
`
